/** @format */

import React from 'react';
import PropTypes from 'fe-prop-types';
import styled, { css } from 'styled-components';
import { SvgIcon } from 'fe-comp-svg-icon';
import theme from 'fe-website-lib-theme';

const PROP_TYPES = {
  isOpen: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
  controls: PropTypes.string,
};

const ICON_SIZE = '20';
const ANIMATION_DURATION = '0.2s';
const ANIMATION_END_ROTATION = '45';
const ANIMATION_TOP_TRANSLATE_X = '3';
const ANIMATION_TOP_TRANSLATE_Y = '11';
const ANIMATION_BOTTOM_TRANSLATE_X = '10';
const ANIMATION_BOTTOM_TRANSLATE_Y = '-9';
const STROKE_COLOUR = theme.colorCopy;

// Detect Internet Explorer
const isIE = Boolean(document.documentMode);

const MenuButton = styled.button`
  background: transparent;
  cursor: pointer;
  border: 0;

  .top-line,
  .bottom-line {
    transition: transform ${ANIMATION_DURATION} ease-out;
  }

  .center-line {
    opacity: 1;
    transition: opacity ${ANIMATION_DURATION} ease-out;
  }

  .top-line {
    ${(props) =>
      props.isOpen &&
      css`
        transform: translate(
            ${ANIMATION_TOP_TRANSLATE_X}px,
            ${ANIMATION_TOP_TRANSLATE_Y}px
          )
          rotate(-${ANIMATION_END_ROTATION}deg);
      `};
  }

  .center-line {
    ${(props) =>
      props.isOpen &&
      css`
        opacity: 0;
      `};
  }

  .bottom-line {
    ${(props) =>
      props.isOpen &&
      css`
        transform: translate(
            ${ANIMATION_BOTTOM_TRANSLATE_X}px,
            ${ANIMATION_BOTTOM_TRANSLATE_Y}px
          )
          rotate(${ANIMATION_END_ROTATION}deg);
      `};
  }

  &:focus {
    outline: none;
  }
`;

const MenuIcon = styled(SvgIcon)`
  background: transparent;
`;

const MenuLinesLightIconGenerator = (isIEandOpen) => {
  // Note: it is important to assign the generated function to a const
  // so that the functional component has a name
  const MenuLinesLight = () => (
    <g transform="translate(1, 5)">
      <path
        id="topLine"
        className="top-line"
        stroke={STROKE_COLOUR}
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        d="M0.514718626,0 L17.4852814,0"
        transform={
          isIEandOpen
            ? `translate(${ANIMATION_TOP_TRANSLATE_X} ${ANIMATION_TOP_TRANSLATE_Y}) rotate(-${ANIMATION_END_ROTATION})`
            : null
        }
      />
      <path
        id="centerLine"
        className="center-line"
        stroke={STROKE_COLOUR}
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        d="M0.514718626,5 L17.4852814,5"
      />
      <path
        id="bottomLine"
        className="bottom-line"
        stroke={STROKE_COLOUR}
        strokeWidth="2.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        d="M17.4852814,10 L0.514718626,10"
        transform={
          isIEandOpen
            ? `translate(${ANIMATION_BOTTOM_TRANSLATE_X} ${ANIMATION_BOTTOM_TRANSLATE_Y}) rotate(${ANIMATION_END_ROTATION})`
            : null
        }
      />
    </g>
  );
  return MenuLinesLight;
};

// Note: we can't use this generator function directly in the Hamburger functional component,
// because the icon would then be re-generated every time isOpen changes,
// which breaks CSS animations (the underlying DOM elements are destroyed & recreated)
const MenuLinesLight = MenuLinesLightIconGenerator(false);
const MenuLinesLightOpenIE = MenuLinesLightIconGenerator(true);

const Hamburger = ({ isOpen, onClick, controls }) => {
  return (
    <MenuButton
      isOpen={isOpen}
      className={isOpen ? 'is-open' : ''}
      onClick={onClick}
      aria-controls={controls}
      aria-expanded={isOpen}
      data-it="DrawerButton"
    >
      <MenuIcon
        glyph={isIE && isOpen ? MenuLinesLightOpenIE : MenuLinesLight}
        fill={STROKE_COLOUR}
        size={ICON_SIZE}
        titleId="mobile-nav-menu-icon"
        title="Reveal Navigation"
      />
    </MenuButton>
  );
};

Hamburger.propTypes = PROP_TYPES;

export { Hamburger };
